import { createHash } from "node:crypto";

import type {
  WorkGraphAuditMethod,
  WorkGraphClientExtractionSummary,
  WorkGraphFinding,
  WorkGraphImpactProjection,
  WorkGraphRecurringPattern,
  WorkGraphSourceCoverage,
  WorkGraphSourceEvent,
  WorkGraphTrail,
  WorkGraphTrailRecommendation,
} from "./work-graph.js";

export const WORK_GRAPH_INVESTIGATION_SCHEMA_VERSION = "2.0.0" as const;

export const WORK_GRAPH_INVESTIGATION_CLIENTS = [
  "claude_code",
  "codex",
  "opencode",
  "goose",
  "cursor",
  "github",
  "slack",
  "orgx_runtime_hook",
] as const;

export type WorkGraphInvestigationClient =
  (typeof WORK_GRAPH_INVESTIGATION_CLIENTS)[number];

export type WorkGraphProvider =
  | "anthropic"
  | "openai"
  | "google"
  | "ollama"
  | "openrouter"
  | "azure"
  | "bedrock"
  | "unknown";

export type SourceStatus =
  | "connected"
  | "supported_not_connected"
  | "detected_not_read"
  | "partial"
  | "missing"
  | "unsupported"
  | "unknown";

export type ExtractionMode =
  | "native"
  | "sqlite"
  | "jsonl"
  | "json"
  | "export"
  | "shared_link"
  | "terminal_log"
  | "orgx_runtime_hook"
  | "mcp_extension"
  | "specstory"
  | "unknown";

export type SkipReason =
  | "binary_or_corrupt"
  | "too_short_lt_2_turns"
  | "no_assistant_turns"
  | "no_entity_references"
  | "pre_cutoff_window"
  | "permission_denied"
  | "schema_version_unsupported";

export type RawEventKind =
  | "user_prompt"
  | "assistant_text"
  | "assistant_reasoning"
  | "tool_call_start"
  | "tool_call_result"
  | "tool_call_error"
  | "file_edit"
  | "file_read"
  | "shell_command"
  | "test_run"
  | "compaction"
  | "session_start"
  | "session_resume"
  | "session_fork"
  | "session_end"
  | "commit"
  | "pr_event"
  | "slack_message"
  | "mcp_tool_call"
  | "error_emitted"
  | "permission_request"
  | "permission_grant"
  | "permission_deny";

export interface SourceRef {
  source_id: WorkGraphInvestigationClient;
  uri: string;
  cite_url: string | null;
  resolves: boolean;
}

export interface RawEvent {
  event_id: string;
  source_ref: SourceRef;
  source_id: WorkGraphInvestigationClient;
  provider: WorkGraphProvider;
  session_id: string;
  project_id: string | null;
  timestamp: string;
  occurred_in_window:
    | "live"
    | "recent_24h"
    | "recent_7d"
    | "recent_30d"
    | "older";
  kind: RawEventKind;
  role: "user" | "assistant" | "system" | "tool" | "meta";
  payload: Record<string, unknown>;
  raw_byte_offset: number | null;
  raw_row_id: string | null;
  content_hash: string;
  redaction_applied: boolean;
}

export interface SourceAdapter {
  source_id: WorkGraphInvestigationClient;
  brand: BrandRef;
  capability_profile: SourceCapabilityCeilings;
  discover(env: HostEnv): Corpus;
  read(corpus: Corpus): RawEvent[];
  classify(event: RawEvent): RawEvent;
  health(corpus: Corpus): SourceHealth;
}

export interface HostEnv {
  cwd: string;
  home: string;
  now: string;
}

export interface BrandRef {
  name: string;
  simple_icons: string | null;
  color: string;
  logo_kind: "symbol" | "wordmark" | "wordmark+symbol";
  asset_path?: string;
}

export interface Corpus {
  source_id: WorkGraphInvestigationClient;
  extraction_mode: ExtractionMode;
  storage_paths: string[];
  storage_version_detected: string | null;
  files_seen: number;
  files_readable: number;
  files_skipped: Array<{ path: string; reason: SkipReason }>;
  bytes_total: number;
  earliest_timestamp: string | null;
  latest_timestamp: string | null;
}

export interface SourceHealth {
  source_id: WorkGraphInvestigationClient;
  status: SourceStatus;
  extraction_mode: ExtractionMode;
  detected_paths: number;
  readable_paths: number;
  limitations: string[];
  capability_cells: CapabilityCells;
}

export type CapabilityDimension =
  | "chronology"
  | "ownership"
  | "verification"
  | "handoff"
  | "customer_context"
  | "decision_lineage";

export interface CapabilityCell {
  ceiling: "high" | "medium" | "low" | "none";
  achieved: number;
  basis: string;
  blockers_to_full: string[];
}

export type CapabilityCells = Record<CapabilityDimension, CapabilityCell>;
export type SourceCapabilityCeilings = Record<
  CapabilityDimension,
  CapabilityCell["ceiling"]
>;

export interface CorpusManifest {
  audit_id: string;
  ran_at: string;
  sources: SourceManifest[];
  byClient: Record<
    WorkGraphInvestigationClient,
    {
      detected: boolean;
      connected: boolean;
      status: SourceStatus;
      extractionMode: ExtractionMode;
      provider: WorkGraphProvider;
      sessions: number;
      turns: number;
      toolCalls: number;
      mcpToolCalls: number;
      commands: number;
      fileEdits: number;
      tests: number;
      errors: number;
      skipped: number;
      limitations: string[];
      schemaVersionDetected: string | null;
    }
  >;
  totals: {
    files_seen: number;
    files_read: number;
    files_skipped: number;
    bytes_read: number;
    sessions: number;
    sessions_skipped: Array<{ reason: SkipReason; count: number }>;
    message_turns: number;
    user_prompts: number;
    assistant_turns: number;
    assistant_reasoning_turns: number;
    tool_calls: number;
    mcp_tool_calls: number;
    tool_call_errors: number;
    file_edits: number;
    file_reads: number;
    shell_commands: number;
    test_runs: number;
    errors_emitted: number;
    permission_requests: number;
    forks: number;
    compactions: number;
    earliest_event: string | null;
    latest_event: string | null;
  };
}

export interface SourceManifest {
  source_id: WorkGraphInvestigationClient;
  brand: BrandRef;
  client: WorkGraphInvestigationClient;
  provider: WorkGraphProvider;
  status: SourceStatus;
  extraction_mode: ExtractionMode;
  storage_paths_resolved: string[];
  storage_version_detected: string | null;
  files_seen: number;
  files_read: number;
  files_skipped: Array<{ reason: SkipReason; count: number }>;
  sessions: number;
  message_turns: number;
  tool_calls: number;
  mcp_tool_calls: number;
  capability_cells: CapabilityCells;
}

export type IntentClass =
  | "feature_implementation"
  | "bug_fix"
  | "architecture_decision"
  | "verification_or_testing"
  | "integration_or_connection"
  | "cleanup_or_refactor"
  | "investigation"
  | "documentation"
  | "configuration_change"
  | "unclear";

export type BottleneckClass =
  | "schema_validation"
  | "permission_denied"
  | "tool_misconfigured"
  | "context_overflow"
  | "auth_or_billing"
  | "race_or_dispatch"
  | "cross_repo_dependency"
  | "external_api_failure"
  | "human_handoff_missing"
  | "unknown";

export interface ProofState {
  kind:
    | "commit_landed"
    | "test_passed"
    | "file_edit_no_revert"
    | "mcp_writeback_succeeded"
    | "inferred_only"
    | "absent";
  ref?: string;
  expected_kind?: string;
}

export interface WorkLoop {
  loop_id: string;
  cites: string[];
  origin: {
    event_id: string;
    timestamp: string;
    intent: string;
    intent_class: IntentClass;
  };
  path: {
    event_ids: string[];
    actions_taken: Array<{ kind: string; label: string; event_id: string }>;
    sessions_spanned: string[];
    tools_used: string[];
    span_minutes: number;
  };
  terminal: {
    state: "shipped" | "blocked" | "abandoned" | "forgotten" | "ongoing";
    proof: ProofState;
    classified_by: "deterministic" | "llm" | "hybrid";
    classification_basis: string[];
  };
  bottleneck: {
    class: BottleneckClass | null;
    evidence_event_ids: string[];
  };
  recurrence: {
    family_id: string | null;
    appearances_in_family: number;
    is_first_appearance: boolean;
    decay_half_life_days: number | null;
  };
  confidence: number;
  survived_critic: boolean;
  public_surface: boolean;
}

export type LoopShape =
  | "dense_recent_cluster"
  | "chronic_evenly_spaced"
  | "zombie_revival"
  | "accelerating"
  | "decaying_resolved"
  | "single_signal";

export interface LoopFamily {
  family_id: string;
  shared_intent_class: IntentClass;
  shared_terminal_state: WorkLoop["terminal"]["state"];
  shared_bottleneck_class: BottleneckClass | null;
  loop_ids: string[];
  appearances: number;
  span_days: number;
  shape: LoopShape;
  semantic_centroid: string;
  divergence_score: number;
  cross_source_confluence: boolean;
  orgx_repair: OrgXRepair;
  alternative_explanations_considered: string[];
  alternative_rejected_because: string[];
  survived_critic: boolean;
  public_surface: boolean;
}

export interface OrgXRepair {
  named_orgx_capability: string;
  expected_tool_call_args: Record<string, unknown>;
  expected_outcome: string;
  expected_score_lift: Array<{ dimension: string; delta: number }>;
  expected_hours_recovered: number;
  expected_dollars_recovered: number;
  preconditions: string[];
}

export type ToolUsageState =
  | "mentioned"
  | "configured"
  | "available"
  | "invoked"
  | "succeeded"
  | "failed"
  | "retried"
  | "should_have_invoked";

export interface UsageCatalogue {
  ai_clients: Array<{
    source_id: WorkGraphInvestigationClient;
    sessions: number;
    turns: number;
  }>;
  mcp_tools: Array<{
    name: string;
    namespace: string;
    state: ToolUsageState;
    invocations: number;
    success: number;
    error: number;
    error_classes: Array<{ class: string; count: number }>;
    by_source: Array<{ source_id: WorkGraphInvestigationClient; count: number }>;
    event_refs: string[];
  }>;
  mcp_namespaces_seen: string[];
  shell_commands: Array<{
    command_root: string;
    invocations: number;
    by_subcommand: Array<{ sub: string; count: number }>;
    failure_count: number;
    event_refs: string[];
  }>;
  recipes_or_skills: Array<{
    source_id: WorkGraphInvestigationClient;
    name: string;
    invocations: number;
    last_used: string;
  }>;
  agents_observed: Array<{
    source_id: WorkGraphInvestigationClient;
    name: string | null;
    spawn_count: number;
    success_count: number;
  }>;
  repo_paths_touched: Array<{ path: string; edit_count: number; read_count: number }>;
  external_sources_called: Array<{ domain: string; call_count: number; via_tool: string }>;
}

export interface CapabilityMatrix {
  rows: CapabilityDimension[];
  columns: SourceManifest[];
}

export interface WhyNotEntry {
  dimension:
    | "cross_source_confluence"
    | "source_coverage"
    | "chronology_depth"
    | "impact_grounding"
    | "evidence_coverage"
    | "attribution"
    | "insight_depth"
    | "repair_actionability";
  current_score: number;
  precise_gap: string;
  user_action: {
    kind:
      | "connect_source"
      | "install_hook"
      | "invite_collaborator"
      | "enable_extension"
      | "authorize_api";
    target: string;
    ux_route: string;
    copy_for_button: string;
  } | null;
  orgx_action: {
    kind: "auto_correlate" | "auto_dedupe" | "auto_writeback" | "auto_classify";
    description: string;
    triggers_when: "on_user_action_completion" | "on_next_audit_run";
  } | null;
  expected_score_lift: number;
  expected_findings_unlocked: number;
  preview_findings: Array<{
    title_redacted: string;
    evidence_count_anticipated: number;
    confidence_anticipated: number;
    derivation_basis: "peer_pattern" | "extrapolated_from_existing" | "rule_based";
  }>;
  preconditions: string[];
  estimated_seconds_to_complete: number;
}

export interface Counterfactual {
  triggered_by_event_id: string;
  triggered_at: string;
  actual_user_event_summary: string;
  actual_outcome: string;
  orgx_capability: {
    tool: string;
    args: Record<string, unknown>;
    args_basis: string[];
  };
  orgx_outcome: string;
  resulting_entity: {
    kind: "decision" | "initiative" | "artifact" | "task";
    inferred_id: string;
    would_link_to: string[];
  };
  realism_score: number;
  realism_basis: string[];
  could_have_failed_because: string[];
  generated_by_prompt_version: string;
  survived_critic: boolean;
}

export interface CriticPass {
  finding_id: string;
  attack_vectors_attempted: string[];
  weakest_claim: string;
  most_plausible_alternative: string;
  survival_score: number;
  survived: boolean;
  demoted_to: "public" | "weak_signal" | "dropped";
}

export interface VerifyResult {
  finding_id: string;
  citations_total: number;
  citations_resolved: number;
  citations_failed: Array<{ event_id_ref: string; reason: string }>;
  content_hashes_match: boolean;
  passed: boolean;
}

export interface RepairAction {
  id: string;
  type:
    | "promote_decision"
    | "assign_owner"
    | "attach_artifact"
    | "connect_source"
    | "convert_to_initiative"
    | "install_runtime_hook";
  title: string;
  closes_loop_ids: string[];
  expected_gain: string;
  expected_hours_recovered: number;
  expected_dollars_recovered: number;
  required_inputs: string[];
  confidence: number;
  time_to_value: string;
  preview_tree: Array<{ kind: "workstream" | "milestone" | "task"; title: string }>;
}

export interface WorkGraphInvestigationV2 {
  schema_version: typeof WORK_GRAPH_INVESTIGATION_SCHEMA_VERSION;
  audit_id: string;
  fingerprint: string;
  generated_at: string;
  corpus_manifest: CorpusManifest;
  raw_events_summary: {
    count: number;
    by_source: Array<{ source_id: WorkGraphInvestigationClient; count: number }>;
    by_kind: Array<{ kind: RawEventKind; count: number }>;
    earliest: string | null;
    latest: string | null;
  };
  raw_events: RawEvent[];
  work_loops: WorkLoop[];
  loop_families: LoopFamily[];
  usage_catalogue: UsageCatalogue;
  source_confidence: CapabilityMatrix;
  why_not_100: WhyNotEntry[];
  counterfactuals: Counterfactual[];
  critic_log: CriticPass[];
  verification_log: VerifyResult[];
  mirror_paragraph: {
    text: string;
    evidence_pills: Array<{ phrase: string; cites: string[] }>;
  };
  repair_plan: RepairAction[];
  impact_projection: {
    hours_recoverable_per_week: { value: number; basis: string; confidence: number };
    dollars_recoverable_per_month: { value: number; basis: string; confidence: number };
    acceleration_pct: { value: number; basis: string; confidence: number };
  };
  redaction_log: Array<{ kind: string; count: number }>;
  raw_transcripts_excluded: true;
  claimable: boolean;
}

export interface BuildWorkGraphInvestigationInput {
  auditMethod: WorkGraphAuditMethod;
  clientExtractions: readonly WorkGraphClientExtractionSummary[];
  coverage: WorkGraphSourceCoverage;
  events: readonly WorkGraphSourceEvent[];
  findings: readonly WorkGraphFinding[];
  fingerprint: string;
  generatedAt: string;
  impact: WorkGraphImpactProjection;
  patterns: readonly WorkGraphRecurringPattern[];
  rawEvents?: readonly RawEvent[];
  recommendations: readonly WorkGraphTrailRecommendation[];
  trails: readonly WorkGraphTrail[];
}

const BRANDS: Record<WorkGraphInvestigationClient, BrandRef> = {
  claude_code: {
    name: "Claude Code",
    simple_icons: "anthropic",
    color: "#D97757",
    logo_kind: "wordmark+symbol",
  },
  codex: {
    name: "Codex",
    simple_icons: "openai",
    color: "#10A37F",
    logo_kind: "wordmark+symbol",
  },
  opencode: {
    name: "OpenCode",
    simple_icons: null,
    color: "#1A1A1A",
    logo_kind: "wordmark",
    asset_path: "/brands/opencode.svg",
  },
  goose: {
    name: "goose",
    simple_icons: null,
    color: "#FCBF49",
    logo_kind: "wordmark+symbol",
    asset_path: "/brands/goose.svg",
  },
  cursor: {
    name: "Cursor",
    simple_icons: null,
    color: "#FFFFFF",
    logo_kind: "wordmark+symbol",
    asset_path: "/brands/cursor.svg",
  },
  github: {
    name: "GitHub",
    simple_icons: "github",
    color: "#181717",
    logo_kind: "symbol",
  },
  slack: {
    name: "Slack",
    simple_icons: "slack",
    color: "#4A154B",
    logo_kind: "wordmark+symbol",
  },
  orgx_runtime_hook: {
    name: "OrgX Runtime",
    simple_icons: null,
    color: "#00C9A7",
    logo_kind: "wordmark+symbol",
    asset_path: "/brands/orgx.svg",
  },
};

const CAPABILITY_CEILINGS: Record<WorkGraphInvestigationClient, SourceCapabilityCeilings> = {
  claude_code: {
    chronology: "high",
    ownership: "none",
    verification: "low",
    handoff: "medium",
    customer_context: "none",
    decision_lineage: "medium",
  },
  codex: {
    chronology: "high",
    ownership: "none",
    verification: "low",
    handoff: "medium",
    customer_context: "none",
    decision_lineage: "medium",
  },
  opencode: {
    chronology: "high",
    ownership: "none",
    verification: "medium",
    handoff: "medium",
    customer_context: "none",
    decision_lineage: "high",
  },
  goose: {
    chronology: "high",
    ownership: "low",
    verification: "medium",
    handoff: "low",
    customer_context: "low",
    decision_lineage: "high",
  },
  cursor: {
    chronology: "medium",
    ownership: "none",
    verification: "low",
    handoff: "low",
    customer_context: "none",
    decision_lineage: "medium",
  },
  github: {
    chronology: "high",
    ownership: "high",
    verification: "high",
    handoff: "none",
    customer_context: "low",
    decision_lineage: "medium",
  },
  slack: {
    chronology: "medium",
    ownership: "high",
    verification: "none",
    handoff: "high",
    customer_context: "high",
    decision_lineage: "low",
  },
  orgx_runtime_hook: {
    chronology: "high",
    ownership: "medium",
    verification: "high",
    handoff: "high",
    customer_context: "none",
    decision_lineage: "high",
  },
};

function hash(value: unknown, length = 16): string {
  return createHash("sha256").update(JSON.stringify(value)).digest("hex").slice(0, length);
}

function clamp(value: number, min = 0, max = 1): number {
  return Math.max(min, Math.min(max, value));
}

function compactText(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function stripSourceSyntax(value: string): string {
  return compactText(
    value
      .replace(/[`*_#>]/g, "")
      .replace(/\b[A-Z]+:\s*\[[^\]]+\]/g, "")
      .replace(/\[[^\]]*(?:brief|placeholder|todo|impact|summary)[^\]]*\]/gi, "")
      .replace(/\b(?:Impact|Summary|Result|TODO)\s*:\s*$/i, ""),
  );
}

function isPlaceholderIntent(value: string): boolean {
  const text = value.trim();
  return (
    /\[(?:brief|placeholder|todo|impact|summary)[^\]]*\]/i.test(text) ||
    /^(?:impact|summary|result|todo)\s*:\s*(?:$|\[[^\]]+\])/i.test(text)
  );
}

function isCodeFragmentIntent(value: string): boolean {
  const text = value.trim();
  if (/^[{[\]}(),:;'"`|\\/-]+/.test(text)) return true;
  if (/[{};]/.test(text) && text.length < 180) return true;
  if (/^[a-zA-Z_$][\w$]*\s*[:,]/.test(text) && /[,{}]/.test(text)) return true;
  if (/^(?:true|false|null|undefined|\d+)$/.test(text)) return true;
  return false;
}

function isLowQualityIntent(value: string): boolean {
  const text = value.trim();
  return (
    text.length < 18 ||
    isPlaceholderIntent(text) ||
    isCodeFragmentIntent(text) ||
    /^[\W_]+$/.test(text)
  );
}

function cleanLoopIntent(value: string, fallback = "Unclassified AI work loop"): string {
  const cleaned = stripSourceSyntax(value)
    .replace(/\bmcporgx\b/gi, "OrgX MCP")
    .replace(/\bmcp__orgx__([a-z0-9_]+)/gi, "OrgX MCP $1")
    .replace(/_/g, " ");
  if (!cleaned || isLowQualityIntent(cleaned)) return fallback;
  return cleaned.slice(0, 160);
}

function loopTopicKey(loop: WorkLoop): string {
  const intent = cleanLoopIntent(loop.origin.intent, "");
  const text = `${intent} ${loop.path.tools_used.join(" ")} ${loop.bottleneck.class ?? ""}`.toLowerCase();
  const topicRules: Array<[RegExp, string]> = [
    [/\blist entities\b|\blist_entities\b|\bschema validation\b|\bzod\b/, "orgx-list-entities-schema"],
    [/\bscaffold\b|\binitiative\b|\boperation qa loop\b/, "scaffold-initiative-loop"],
    [/\bship batch\b|\bship_batch\b|\bartifacturl\b|\bexternalurl\b/, "ship-batch-artifact-contract"],
    [/\bruntime hook\b|\bwriteback\b|\brecord outcome\b|\bsubmit learning\b/, "runtime-writeback"],
    [/\b64\b.*\btools\b|\bmcp tools\b|\btool catalog\b/, "mcp-tool-catalog"],
    [/\banthropic\b|\bapi credits\b|\bbilling\b|\bquota\b/, "provider-auth-billing"],
    [/\bgithub\b|\bcommit\b|\bpr\b|\bproof\b/, "github-proof"],
    [/\bpublic profile\b|\bwork graph\b|\bmirror\b|\bclaim\b/, "work-graph-profile"],
    [/\bcursor\b/, "cursor-client"],
    [/\bcodex\b/, "codex-client"],
    [/\bclaude\b/, "claude-code-client"],
  ];
  for (const [pattern, topic] of topicRules) {
    if (pattern.test(text)) return topic;
  }
  if (!intent) return `unclassified:${hash(loop.loop_id, 10)}`;
  return `topic:${hash(intent.toLowerCase(), 10)}`;
}

function bestFamilyCentroid(group: readonly WorkLoop[]): string {
  const candidates = group
    .map((loop) => cleanLoopIntent(loop.origin.intent, ""))
    .filter(Boolean)
    .map((intent) => {
      let score = 0;
      if (!isLowQualityIntent(intent)) score += 20;
      if (/\borgx|mcp|codex|claude|cursor|github|slack|runtime|hook|scaffold|profile|wizard/i.test(intent)) score += 8;
      if (intent.length >= 32 && intent.length <= 120) score += 4;
      if (/[{}\[\];]/.test(intent)) score -= 20;
      if (/^(?:impact|summary|result)\s*:/i.test(intent)) score -= 16;
      return { intent, score };
    })
    .sort((left, right) => right.score - left.score);
  return candidates[0]?.intent || "A repeated AI work loop needs durable writeback";
}

function articleFor(value: string): "a" | "an" {
  return /^[aeiou]/i.test(value.trim()) ? "an" : "a";
}

function countBy<T extends string>(values: readonly T[]): Array<{ key: T; count: number }> {
  const counts = new Map<T, number>();
  for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1);
  return [...counts.entries()].map(([key, count]) => ({ key, count }));
}

function normalizeClient(value: string): WorkGraphInvestigationClient {
  const lower = value.toLowerCase().replace(/-/g, "_");
  if (lower.includes("claude")) return "claude_code";
  if (lower.includes("codex") || lower.includes("openai")) return "codex";
  if (lower.includes("cursor")) return "cursor";
  if (lower.includes("opencode")) return "opencode";
  if (lower.includes("goose")) return "goose";
  if (lower.includes("github") || lower === "git") return "github";
  if (lower.includes("slack")) return "slack";
  if (lower.includes("hook") || lower.includes("runtime") || lower.includes("mcp")) return "orgx_runtime_hook";
  return "codex";
}

function providerForClient(client: WorkGraphInvestigationClient): WorkGraphProvider {
  if (client === "claude_code") return "anthropic";
  if (client === "codex") return "openai";
  return "unknown";
}

function eventKindForFinding(finding: WorkGraphFinding): RawEventKind {
  if (finding.source_client === "github") return finding.type === "outcome" ? "test_run" : "commit";
  if (finding.source_client === "slack") return "slack_message";
  if (finding.source_client === "orgx_runtime_hook" || finding.source_client === "mcp") return "mcp_tool_call";
  if (finding.type === "decision") return "assistant_reasoning";
  if (finding.type === "artifact") return "file_edit";
  if (finding.type === "blocker") return "tool_call_error";
  if (finding.type === "outcome") return "test_run";
  if (finding.type === "missed_orchestration_opportunity") return "mcp_tool_call";
  return "assistant_text";
}

function windowFor(timestamp: string, now: string): RawEvent["occurred_in_window"] {
  const occurred = Date.parse(timestamp);
  const current = Date.parse(now);
  if (!Number.isFinite(occurred) || !Number.isFinite(current)) return "older";
  const delta = current - occurred;
  if (delta < 5 * 60 * 1000) return "live";
  if (delta <= 24 * 60 * 60 * 1000) return "recent_24h";
  if (delta <= 7 * 24 * 60 * 60 * 1000) return "recent_7d";
  if (delta <= 30 * 24 * 60 * 60 * 1000) return "recent_30d";
  return "older";
}

function rawEventFromFinding(
  finding: WorkGraphFinding,
  index: number,
  generatedAt: string,
): RawEvent {
  const sourceClient = normalizeClient(finding.source_client);
  const timestamp =
    typeof finding.metadata.occurred_at === "string"
      ? finding.metadata.occurred_at
      : generatedAt;
  const eventId = `evt_${hash([finding.evidence_ref, finding.title, index], 24)}`;
  const payload = {
    title: finding.title,
    summary: finding.summary,
    finding_type: finding.type,
    source_id: finding.source_id,
    confidence: finding.confidence,
    commit_sha: finding.metadata.commit_sha,
    owner_ref: finding.metadata.owner_ref,
    proof_kind: finding.metadata.proof_kind,
    writeback_ref: finding.metadata.writeback_ref,
    redacted_verbatim:
      typeof finding.metadata.redacted_verbatim === "string"
        ? finding.metadata.redacted_verbatim
        : finding.summary.slice(0, 420),
  };
  return {
    event_id: eventId,
    source_ref: {
      source_id: sourceClient,
      uri: finding.evidence_ref,
      cite_url: null,
      resolves: true,
    },
    source_id: sourceClient,
    provider: providerForClient(sourceClient),
    session_id: finding.source_id,
    project_id: null,
    timestamp,
    occurred_in_window: windowFor(timestamp, generatedAt),
    kind: eventKindForFinding(finding),
    role: finding.type === "blocker" ? "tool" : "meta",
    payload,
    raw_byte_offset: null,
    raw_row_id: null,
    content_hash: hash(payload, 64),
    redaction_applied: true,
  };
}

function rawEventFromSourceEvent(
  event: WorkGraphSourceEvent,
  index: number,
  generatedAt: string,
): RawEvent {
  const sourceClient = normalizeClient(event.source_client);
  const payload = {
    source_label: event.source_label,
    event_type: event.event_type,
    text_summary: event.text.slice(0, 420),
  };
  return {
    event_id: `evt_${hash([event.evidence_ref, index], 24)}`,
    source_ref: {
      source_id: sourceClient,
      uri: event.evidence_ref,
      cite_url: null,
      resolves: true,
    },
    source_id: sourceClient,
    provider: providerForClient(sourceClient),
    session_id: event.source_id,
    project_id: null,
    timestamp: generatedAt,
    occurred_in_window: "live",
    kind: event.event_type === "tool_signal" ? "tool_call_start" : "assistant_text",
    role: "meta",
    payload,
    raw_byte_offset: null,
    raw_row_id: null,
    content_hash: hash(payload, 64),
    redaction_applied: true,
  };
}

function buildRawEvents(input: BuildWorkGraphInvestigationInput): RawEvent[] {
  const sourceEvents = input.events.map((event, index) =>
    rawEventFromSourceEvent(event, index, input.generatedAt),
  );
  const findingEvents = input.findings.map((finding, index) =>
    rawEventFromFinding(finding, index, input.generatedAt),
  );
  const byUri = new Map<string, RawEvent>();
  for (const event of [...(input.rawEvents ?? []), ...sourceEvents, ...findingEvents]) {
    byUri.set(event.event_id || `${event.source_ref.uri}:${event.kind}`, event);
  }
  return [...byUri.values()].sort((left, right) =>
    left.timestamp.localeCompare(right.timestamp),
  );
}

function eventsForRefs(events: readonly RawEvent[], refs: readonly string[]): RawEvent[] {
  const refSet = new Set(refs);
  return events.filter((event) => refSet.has(event.source_ref.uri));
}

function intentClassForFinding(finding: WorkGraphFinding): IntentClass {
  const text = `${finding.title}\n${finding.summary}`;
  if (finding.type === "decision") return "architecture_decision";
  if (finding.type === "artifact" || /\b(implemented|built|created|shipped)\b/i.test(text)) {
    return "feature_implementation";
  }
  if (/\b(test|qa|verified|proof)\b/i.test(text)) return "verification_or_testing";
  if (/\b(auth|config|mcp|source|connect|integration|hook)\b/i.test(text)) {
    return "integration_or_connection";
  }
  if (/\b(refactor|cleanup|rename|remove)\b/i.test(text)) return "cleanup_or_refactor";
  if (/\b(doc|readme|methodology|runbook)\b/i.test(text)) return "documentation";
  if (finding.type === "blocker" && /\bbug|error|fail|zod|schema\b/i.test(text)) return "bug_fix";
  if (finding.type === "blocker") return "investigation";
  return "unclear";
}

function bottleneckClassForText(text: string): BottleneckClass | null {
  if (/\bzod\b|schema|validation|expected .* received/i.test(text)) return "schema_validation";
  if (/permission|approval|denied|unauthorized|401|403/i.test(text)) return "permission_denied";
  if (/missing arg|wrong type|tool.*misconfigured|invalid/i.test(text)) return "tool_misconfigured";
  if (/context overflow|compaction|too large/i.test(text)) return "context_overflow";
  if (/auth|billing|quota|402|api key|token/i.test(text)) return "auth_or_billing";
  if (/dispatch|race|not dispatched|ready stream/i.test(text)) return "race_or_dispatch";
  if (/cross.repo|repo boundary|vendored|mirror/i.test(text)) return "cross_repo_dependency";
  if (/external|api failed|timeout|timed out/i.test(text)) return "external_api_failure";
  if (/owner|handoff|dri|approval/i.test(text)) return "human_handoff_missing";
  return null;
}

function terminalForTrail(
  trail: WorkGraphTrail,
  events: readonly RawEvent[] = [],
  findings: readonly WorkGraphFinding[] = [],
): WorkLoop["terminal"] {
  const proofRef = trail.evidence_refs[0];
  const proofFromEvent = events.find((event) => event.kind === "commit" || event.kind === "pr_event" || event.kind === "test_run");
  const mcpWriteback = events.find((event) =>
    event.kind === "mcp_tool_call" &&
    /\borgx|complete_with_proof|record_outcome|writeback|orgx_emit_activity\b/i.test(
      `${event.payload.tool_name ?? ""}\n${event.payload.text_summary ?? ""}`,
    )
  );
  const githubFinding = findings.find((finding) =>
    finding.source_client === "github" || /commit_landed|pull request|github|commit/i.test(JSON.stringify(finding.metadata ?? {})),
  );
  const runtimeFinding = findings.find((finding) =>
    finding.source_client === "orgx_runtime_hook" ||
    finding.source_client === "mcp" ||
    /mcp_writeback_succeeded|complete_with_proof|record_outcome|orgx_emit_activity/i.test(JSON.stringify(finding.metadata ?? {})),
  );
  const proofState = (kind: ProofState["kind"], ref: string | undefined): ProofState =>
    ref ? { kind, ref } : { kind };
  const verifiedProof: ProofState | null =
    proofFromEvent?.kind === "commit" || proofFromEvent?.kind === "pr_event" || githubFinding
      ? proofState("commit_landed", proofFromEvent?.event_id ?? githubFinding?.evidence_ref ?? proofRef)
      : proofFromEvent?.kind === "test_run"
        ? proofState("test_passed", proofFromEvent.event_id)
        : mcpWriteback || runtimeFinding
          ? proofState("mcp_writeback_succeeded", mcpWriteback?.event_id ?? runtimeFinding?.evidence_ref ?? proofRef)
          : null;

  if (verifiedProof) {
    return {
      state: "shipped",
      proof: verifiedProof,
      classified_by: "deterministic",
      classification_basis: ["terminal_proof_event_present"],
    };
  }
  if (trail.state === "verified" || trail.valence === "healthy") {
    return {
      state: "shipped",
      proof: trail.evidence_refs[0]
        ? { kind: "inferred_only", ref: trail.evidence_refs[0] }
        : { kind: "inferred_only" },
      classified_by: "deterministic",
      classification_basis: ["verified_or_healthy_trail_state"],
    };
  }
  if (trail.state === "blocked" || trail.valence === "risk" || trail.valence === "escalating") {
    return {
      state: "blocked",
      proof: { kind: "absent", expected_kind: "owner_visible_resolution" },
      classified_by: "deterministic",
      classification_basis: ["blocked_or_escalating_trail_state"],
    };
  }
  if (trail.state === "stale" || trail.state === "decaying") {
    return {
      state: "abandoned",
      proof: { kind: "absent", expected_kind: "later_resolution_event" },
      classified_by: "deterministic",
      classification_basis: ["stale_or_decaying_trail_state"],
    };
  }
  return {
    state: "ongoing",
    proof: trail.evidence_refs[0]
      ? { kind: "inferred_only", ref: trail.evidence_refs[0] }
      : { kind: "inferred_only" },
    classified_by: "hybrid",
    classification_basis: ["no_deterministic_terminal_event"],
  };
}

function buildWorkLoops(input: {
  events: readonly RawEvent[];
  findings: readonly WorkGraphFinding[];
  trails: readonly WorkGraphTrail[];
}): WorkLoop[] {
  const findingsByRef = new Map(input.findings.map((finding) => [finding.evidence_ref, finding]));
  return input.trails.map((trail, index) => {
    const matchedEvents = eventsForRefs(input.events, trail.evidence_refs);
    const matchedFindings = trail.evidence_refs
      .map((ref) => findingsByRef.get(ref))
      .filter((finding): finding is WorkGraphFinding => Boolean(finding));
    const firstFinding = matchedFindings[0];
    const eventIds = matchedEvents.map((event) => event.event_id);
    const intent = cleanLoopIntent(
      trail.title,
      `${trail.subject_entity_type.replace(/_/g, " ")} work loop needs review`,
    );
    const intentQualityOk = !isLowQualityIntent(trail.title) && !isLowQualityIntent(intent);
    const text = [
      trail.title,
      trail.summary,
      ...matchedFindings.map((finding) => finding.summary),
    ].join("\n");
    const bottleneck = bottleneckClassForText(text);
    const terminal = terminalForTrail(trail, matchedEvents, matchedFindings);
    const confidence = clamp(
      trail.confidence ||
        matchedFindings.reduce((total, finding) => total + finding.confidence, 0) /
          Math.max(1, matchedFindings.length),
    );

    return {
      loop_id: `loop_${hash([trail.id, index], 18)}`,
      cites: eventIds,
      origin: {
        event_id: eventIds[0] ?? `evt_missing_${index}`,
        timestamp: matchedEvents[0]?.timestamp ?? trail.created_at,
        intent,
        intent_class: firstFinding ? intentClassForFinding(firstFinding) : "unclear",
      },
      path: {
        event_ids: eventIds,
        actions_taken: matchedEvents.map((event) => ({
          kind: event.kind,
          label: String(event.payload.title ?? event.kind).slice(0, 120),
          event_id: event.event_id,
        })),
        sessions_spanned: [...new Set(matchedEvents.map((event) => event.session_id))],
        tools_used: matchedEvents
          .filter((event) => event.kind === "tool_call_start" || event.kind === "mcp_tool_call")
          .map((event) => String(event.payload.tool_name ?? event.payload.title ?? "unknown_tool")),
        span_minutes: Math.max(1, matchedEvents.length * 12),
      },
      terminal,
      bottleneck: {
        class: bottleneck,
        evidence_event_ids: bottleneck ? eventIds : [],
      },
      recurrence: {
        family_id: null,
        appearances_in_family: 1,
        is_first_appearance: true,
        decay_half_life_days: terminal.state === "blocked" ? 3 : null,
      },
      confidence,
      survived_critic: confidence >= 0.6 && eventIds.length > 0,
      public_surface: confidence >= 0.6 && eventIds.length > 0 && intentQualityOk,
    };
  });
}

function loopFamilyKey(loop: WorkLoop): string {
  return [
    loop.origin.intent_class,
    loop.terminal.state,
    loop.bottleneck.class ?? "none",
    loopTopicKey(loop),
  ].join(":");
}

function shapeForFamily(loops: readonly WorkLoop[]): LoopShape {
  if (loops.length <= 1) return "single_signal";
  if (loops.some((loop) => loop.terminal.state === "shipped")) return "decaying_resolved";
  if (loops.some((loop) => loop.bottleneck.class)) return loops.length >= 3 ? "accelerating" : "dense_recent_cluster";
  return loops.length >= 4 ? "chronic_evenly_spaced" : "dense_recent_cluster";
}

function repairForFamily(
  familyLoops: readonly WorkLoop[],
  impact: WorkGraphImpactProjection,
): OrgXRepair {
  const top = familyLoops[0];
  const title = top?.origin.intent ?? "Investigate work loop";
  const bottleneck = top?.bottleneck.class;
  const capability =
    bottleneck === "human_handoff_missing"
      ? "mcp__orgx__assign_owner"
      : bottleneck
        ? "mcp__orgx__create_blocker"
        : top?.origin.intent_class === "architecture_decision"
          ? "mcp__orgx__create_decision"
          : "mcp__orgx__scaffold_initiative";

  return {
    named_orgx_capability: capability,
    expected_tool_call_args: {
      title,
      source: "work_graph_investigation",
      evidence_event_ids: familyLoops.flatMap((loop) => loop.cites).slice(0, 12),
      terminal_state: top?.terminal.state ?? "ongoing",
      bottleneck_class: bottleneck ?? null,
    },
    expected_outcome:
      "OrgX would create an owner-visible record tied to the source evidence, then suppress duplicate rediscovery until it is reopened.",
    expected_score_lift: [
      { dimension: "chronology_depth", delta: Math.min(18, familyLoops.length * 3) },
      { dimension: "repair_actionability", delta: capability.includes("scaffold") ? 12 : 16 },
    ],
    expected_hours_recovered: Number(Math.min(impact.time_saved_hours_per_week, familyLoops.length * 0.75).toFixed(1)),
    expected_dollars_recovered: Math.round(Math.min(impact.estimated_monthly_value_usd, familyLoops.length * 0.75 * 4.33 * 200)),
    preconditions: ["claim_profile", "confirm_or_correct_evidence"],
  };
}

function buildLoopFamilies(
  loops: readonly WorkLoop[],
  events: readonly RawEvent[],
  impact: WorkGraphImpactProjection,
): LoopFamily[] {
  const grouped = new Map<string, WorkLoop[]>();
  for (const loop of loops) {
    const key = loopFamilyKey(loop);
    grouped.set(key, [...(grouped.get(key) ?? []), loop]);
  }

  const families = [...grouped.entries()].map(([key, group]) => {
    const sourceIds = new Set(
      group.flatMap((loop) =>
        loop.cites
          .map((cite) => events.find((event) => event.event_id === cite)?.source_id)
          .filter(Boolean),
      ),
    );
    const familyId = `family_${hash(key, 16)}`;
    const timestamps = group.map((loop) => Date.parse(loop.origin.timestamp)).filter(Number.isFinite);
    const spanDays =
      timestamps.length > 1
        ? Math.max(1, Math.ceil((Math.max(...timestamps) - Math.min(...timestamps)) / 86_400_000))
        : 0;
    const publicLoopCount = group.filter((loop) => loop.public_surface).length;
    const semanticCentroid = bestFamilyCentroid(group);
    const divergenceScore = group.length >= 3 && publicLoopCount >= 3 ? 0.32 : 0.5;
    return {
      family_id: familyId,
      shared_intent_class: group[0]?.origin.intent_class ?? "unclear",
      shared_terminal_state: group[0]?.terminal.state ?? "ongoing",
      shared_bottleneck_class: group[0]?.bottleneck.class ?? null,
      loop_ids: group.map((loop) => loop.loop_id),
      appearances: group.length,
      span_days: spanDays,
      shape: shapeForFamily(group),
      semantic_centroid: semanticCentroid,
      divergence_score: divergenceScore,
      cross_source_confluence: sourceIds.size >= 2,
      orgx_repair: repairForFamily(group, impact),
      alternative_explanations_considered: [
        "The loops may be related only by vocabulary.",
        "The apparent recurrence may be a planned multi-step implementation rather than unresolved work.",
      ],
      alternative_rejected_because:
        group.length >= 3
          ? ["Shared intent class, terminal state, and bottleneck class recur across multiple evidence events."]
          : ["Kept as a non-public single/weak family until more chronology exists."],
      survived_critic: group.length >= 3 && publicLoopCount >= 3 && divergenceScore < 0.5,
      public_surface: group.length >= 3 && publicLoopCount >= 3 && divergenceScore < 0.5,
    } satisfies LoopFamily;
  });

  const familyMap = new Map(families.map((family) => [family.family_id, family]));
  for (const family of families) {
    for (const loopId of family.loop_ids) {
      const loop = loops.find((candidate) => candidate.loop_id === loopId);
      if (!loop) continue;
      loop.recurrence.family_id = family.family_id;
      loop.recurrence.appearances_in_family = family.appearances;
      loop.recurrence.is_first_appearance = family.loop_ids[0] === loop.loop_id;
    }
  }

  return [...familyMap.values()].sort((left, right) => right.appearances - left.appearances);
}

function buildUsageCatalogue(events: readonly RawEvent[], findings: readonly WorkGraphFinding[]): UsageCatalogue {
  const toolEvents = events.filter((event) => event.kind === "tool_call_start" || event.kind === "mcp_tool_call");
  const mcpToolNames = new Map<string, RawEvent[]>();
  for (const event of toolEvents) {
    const text = `${event.payload.title ?? ""}\n${event.payload.summary ?? ""}\n${event.payload.text_summary ?? ""}`;
    const match = text.match(/\b(?:mcp__[\w-]+__[\w-]+|orgx_[\w-]+|complete_with_proof|ship_batch|record_outcome|scaffold_initiative)\b/i);
    const name = match?.[0] ?? "unknown_tool";
    mcpToolNames.set(name, [...(mcpToolNames.get(name) ?? []), event]);
  }
  const mentionedTools = new Set<string>();
  for (const finding of findings) {
    const matches = `${finding.title}\n${finding.summary}`.matchAll(/\b(?:mcp__[\w-]+__[\w-]+|orgx_[\w-]+|complete_with_proof|ship_batch|record_outcome|scaffold_initiative)\b/gi);
    for (const match of matches) mentionedTools.add(match[0]!);
  }
  const configuredOrgxEvents = events.filter((event) =>
    event.payload.tool_usage_state === "configured" &&
    /\borgx\b|mcp/i.test(JSON.stringify(event.payload)),
  );
  const shouldHaveInvoked = findings.filter((finding) =>
    finding.type === "missed_orchestration_opportunity" ||
    /\bshould have|available but not called|not invoked|writeback\b/i.test(`${finding.title}\n${finding.summary}`),
  );

  const mcp_tools = [
    ...[...mcpToolNames.entries()].map(([name, matchingEvents]) => ({
      name,
      namespace: name.startsWith("mcp__") ? name.split("__")[1] ?? "unknown" : "orgx",
      state: matchingEvents.length > 0 ? "invoked" as const : "mentioned" as const,
      invocations: matchingEvents.length,
      success: matchingEvents.filter((event) => event.kind === "tool_call_result").length,
      error: matchingEvents.filter((event) => event.kind === "tool_call_error").length,
      error_classes: [],
      by_source: countBy(matchingEvents.map((event) => event.source_id)).map(({ key, count }) => ({
        source_id: key,
        count,
      })),
      event_refs: matchingEvents.map((event) => event.event_id),
    })),
    ...[...mentionedTools]
      .filter((name) => !mcpToolNames.has(name))
      .map((name) => ({
        name,
        namespace: name.startsWith("mcp__") ? name.split("__")[1] ?? "unknown" : "orgx",
        state: "mentioned" as const,
        invocations: 0,
        success: 0,
        error: 0,
        error_classes: [],
        by_source: [],
        event_refs: [],
      })),
    ...(configuredOrgxEvents.length > 0 && !mcpToolNames.has("mcp__orgx__configured")
      ? [{
          name: "mcp__orgx__configured",
          namespace: "orgx",
          state: "configured" as const,
          invocations: 0,
          success: 0,
          error: 0,
          error_classes: [],
          by_source: countBy(configuredOrgxEvents.map((event) => event.source_id)).map(({ key, count }) => ({
            source_id: key,
            count,
          })),
          event_refs: configuredOrgxEvents.map((event) => event.event_id),
        }]
      : []),
    ...(shouldHaveInvoked.length > 0 && !mcpToolNames.has("mcp__orgx__create_decision")
      ? [{
          name: "mcp__orgx__create_decision",
          namespace: "orgx",
          state: "should_have_invoked" as const,
          invocations: 0,
          success: 0,
          error: 0,
          error_classes: [],
          by_source: [],
          event_refs: [],
        }]
      : []),
  ];

  const ai_clients = WORK_GRAPH_INVESTIGATION_CLIENTS.slice(0, 5).map((sourceId) => {
    const sourceEvents = events.filter((event) => event.source_id === sourceId);
    return {
      source_id: sourceId,
      sessions: new Set(sourceEvents.map((event) => event.session_id)).size,
      turns: sourceEvents.filter((event) => event.kind === "user_prompt" || event.kind === "assistant_text").length,
    };
  });

  return {
    ai_clients,
    mcp_tools,
    mcp_namespaces_seen: [...new Set(mcp_tools.map((tool) => tool.namespace))],
    shell_commands: [],
    recipes_or_skills: findings
      .filter((finding) => /\bskill|recipe|agent\b/i.test(`${finding.title}\n${finding.summary}`))
      .slice(0, 20)
      .map((finding) => ({
        source_id: normalizeClient(finding.source_client),
        name: finding.title.slice(0, 80),
        invocations: 0,
        last_used: typeof finding.metadata.occurred_at === "string" ? finding.metadata.occurred_at : new Date().toISOString(),
      })),
    agents_observed: ai_clients
      .filter((client) => client.sessions > 0)
      .map((client) => ({
        source_id: client.source_id,
        name: BRANDS[client.source_id].name,
        spawn_count: client.sessions,
        success_count: 0,
      })),
    repo_paths_touched: [],
    external_sources_called: [],
  };
}

function capabilityCellsFor(input: {
  sourceId: WorkGraphInvestigationClient;
  status: SourceStatus;
  events: readonly RawEvent[];
}): CapabilityCells {
  const ceilings = CAPABILITY_CEILINGS[input.sourceId];
  const sourceEvents = input.events.filter((event) => event.source_id === input.sourceId);
  const achievedBase =
    input.status === "connected"
      ? 0.68
      : input.status === "partial"
        ? 0.42
        : input.status === "detected_not_read"
          ? 0.16
          : 0;
  const hasToolState = sourceEvents.some((event) => event.kind.includes("tool"));
  const hasVerification = sourceEvents.some((event) => event.kind === "test_run" || event.kind === "commit");
  const hasHandoff = sourceEvents.some((event) => /handoff|owner|approval/i.test(JSON.stringify(event.payload)));

  return Object.fromEntries(
    (Object.keys(ceilings) as CapabilityDimension[]).map((dimension) => {
      const ceiling = ceilings[dimension];
      const boost =
        (dimension === "decision_lineage" && hasToolState) ||
        (dimension === "verification" && hasVerification) ||
        (dimension === "handoff" && hasHandoff)
          ? 0.18
          : 0;
      const achieved = ceiling === "none" ? 0 : clamp(achievedBase + boost);
      return [
        dimension,
        {
          ceiling,
          achieved,
          basis:
            sourceEvents.length > 0
              ? `${sourceEvents.length} normalized raw event${sourceEvents.length === 1 ? "" : "s"}`
              : input.status === "missing"
                ? "Source supported but not connected for this audit"
                : "Source detected without enough readable evidence",
          blockers_to_full:
            achieved >= 0.8
              ? []
              : [
                  dimension === "verification"
                    ? "connect_github_or_runtime_hook"
                    : dimension === "ownership" || dimension === "handoff"
                      ? "connect_slack_or_claim_profile"
                      : "deepen_native_session_events",
                ],
        },
      ];
    }),
  ) as CapabilityCells;
}

function collapseSkipped(files: Array<{ path: string; reason: SkipReason }>): Array<{ reason: SkipReason; count: number }> {
  return countBy(files.map((file) => file.reason)).map(({ key, count }) => ({ reason: key, count }));
}

function buildCorpusManifest(input: {
  auditId: string;
  auditMethod: WorkGraphAuditMethod;
  clientExtractions: readonly WorkGraphClientExtractionSummary[];
  coverage: WorkGraphSourceCoverage;
  events: readonly RawEvent[];
  generatedAt: string;
}): CorpusManifest {
  const manifests = WORK_GRAPH_INVESTIGATION_CLIENTS.map<SourceManifest>((sourceId) => {
    const sourceEvents = input.events.filter((event) => event.source_id === sourceId);
    const matchingExtraction = input.clientExtractions.find((extraction) =>
      normalizeClient(extraction.source_client) === sourceId,
    );
    const coverageManifest = input.coverage.manifests?.find((manifest) =>
      normalizeClient(manifest.source_client) === sourceId,
    );
    const status: SourceStatus =
      sourceEvents.length > 0 || (matchingExtraction && matchingExtraction.finding_count > 0) || coverageManifest?.status === "connected"
        ? "connected"
        : matchingExtraction
          ? "detected_not_read"
        : coverageManifest?.status === "partial"
          ? "partial"
          : input.coverage.missing.some((source) => normalizeClient(source) === sourceId)
            ? "missing"
            : "supported_not_connected";
    const skipped = coverageManifest?.skipped_session_count
      ? [{ reason: "no_entity_references" as const, count: coverageManifest.skipped_session_count }]
      : [];
    const cells = capabilityCellsFor({ sourceId, status, events: input.events });
    const sourceSessionCount = new Set(sourceEvents.map((event) => event.session_id)).size;
    return {
      source_id: sourceId,
      brand: BRANDS[sourceId],
      client: sourceId,
      provider: providerForClient(sourceId),
      status,
      extraction_mode:
        sourceId === "opencode" || sourceId === "goose"
          ? "sqlite"
          : sourceId === "cursor"
            ? "specstory"
            : sourceId === "orgx_runtime_hook"
              ? "orgx_runtime_hook"
              : "jsonl",
      storage_paths_resolved: coverageManifest?.searched_sources ?? [],
      storage_version_detected: null,
      files_seen: coverageManifest?.searched_session_count ?? matchingExtraction?.searched_session_count ?? sourceSessionCount,
      files_read: sourceEvents.length > 0 ? Math.max(1, sourceSessionCount) : 0,
      files_skipped: skipped,
      sessions: sourceSessionCount ||
        matchingExtraction?.searched_session_count ||
        coverageManifest?.searched_session_count ||
        0,
      message_turns:
        sourceEvents.filter((event) => event.kind === "user_prompt" || event.kind === "assistant_text").length ||
        (sourceId === "codex" || sourceId === "claude_code" ? input.auditMethod.searched_message_count : 0),
      tool_calls: sourceEvents.filter((event) => event.kind === "tool_call_start").length,
      mcp_tool_calls: sourceEvents.filter((event) => event.kind === "mcp_tool_call").length,
      capability_cells: cells,
    };
  });

  const timestamps = input.events.map((event) => event.timestamp).sort();
  const filesSeen = manifests.reduce((total, source) => total + source.files_seen, 0);
  const filesRead = manifests.reduce((total, source) => total + source.files_read, 0);
  const filesSkipped = manifests.reduce(
    (total, source) => total + source.files_skipped.reduce((sum, skipped) => sum + skipped.count, 0),
    0,
  );
  const sessions = manifests.reduce((total, source) => total + source.sessions, 0);
  const byClient = Object.fromEntries(
    manifests.map((source) => {
      const sourceEvents = input.events.filter((event) => event.source_id === source.source_id);
      const skippedCount = source.files_skipped.reduce((total, skipped) => total + skipped.count, 0);
      const limitations = [
        ...Object.values(source.capability_cells).flatMap((cell) => cell.blockers_to_full),
        ...(source.status === "missing" || source.status === "supported_not_connected" ? ["source_not_connected"] : []),
        ...(source.status === "detected_not_read" ? ["detected_without_readable_events"] : []),
      ];

      return [
        source.source_id,
        {
          detected:
            source.status === "connected" ||
            source.status === "partial" ||
            source.status === "detected_not_read" ||
            source.storage_paths_resolved.length > 0,
          connected: source.status === "connected" || source.status === "partial",
          status: source.status,
          extractionMode: source.extraction_mode,
          provider: source.provider,
          sessions: source.sessions,
          turns: source.message_turns,
          toolCalls: source.tool_calls,
          mcpToolCalls: source.mcp_tool_calls,
          commands: sourceEvents.filter((event) => event.kind === "shell_command").length,
          fileEdits: sourceEvents.filter((event) => event.kind === "file_edit").length,
          tests: sourceEvents.filter((event) => event.kind === "test_run").length,
          errors: sourceEvents.filter((event) => event.kind === "tool_call_error" || event.kind === "error_emitted").length,
          skipped: skippedCount,
          limitations: Array.from(new Set(limitations)).sort(),
          schemaVersionDetected: source.storage_version_detected,
        },
      ];
    }),
  ) as CorpusManifest["byClient"];

  return {
    audit_id: input.auditId,
    ran_at: input.generatedAt,
    sources: manifests,
    byClient,
    totals: {
      files_seen: Math.max(filesSeen, input.auditMethod.searched_session_files),
      files_read: Math.max(filesRead, input.auditMethod.searched_session_files - input.auditMethod.skipped_session_files),
      files_skipped: Math.max(filesSkipped, input.auditMethod.skipped_session_files),
      bytes_read: 0,
      sessions: Math.max(sessions, input.auditMethod.searched_session_files),
      sessions_skipped: input.auditMethod.skipped_session_files
        ? [{ reason: "no_entity_references", count: input.auditMethod.skipped_session_files }]
        : [],
      message_turns: Math.max(
        input.auditMethod.searched_message_count,
        input.events.filter((event) => event.kind === "user_prompt" || event.kind === "assistant_text").length,
      ),
      user_prompts: input.events.filter((event) => event.kind === "user_prompt").length,
      assistant_turns: input.events.filter((event) => event.kind === "assistant_text").length,
      assistant_reasoning_turns: input.events.filter((event) => event.kind === "assistant_reasoning").length,
      tool_calls: input.events.filter((event) => event.kind === "tool_call_start").length,
      mcp_tool_calls: input.events.filter((event) => event.kind === "mcp_tool_call").length,
      tool_call_errors: input.events.filter((event) => event.kind === "tool_call_error").length,
      file_edits: input.events.filter((event) => event.kind === "file_edit").length,
      file_reads: input.events.filter((event) => event.kind === "file_read").length,
      shell_commands: input.events.filter((event) => event.kind === "shell_command").length,
      test_runs: input.events.filter((event) => event.kind === "test_run").length,
      errors_emitted: input.events.filter((event) => event.kind === "error_emitted").length,
      permission_requests: input.events.filter((event) => event.kind === "permission_request").length,
      forks: input.events.filter((event) => event.kind === "session_fork").length,
      compactions: input.events.filter((event) => event.kind === "compaction").length,
      earliest_event: timestamps[0] ?? null,
      latest_event: timestamps[timestamps.length - 1] ?? null,
    },
  };
}

function buildSourceConfidence(corpusManifest: CorpusManifest): CapabilityMatrix {
  return {
    rows: [
      "chronology",
      "ownership",
      "verification",
      "handoff",
      "customer_context",
      "decision_lineage",
    ],
    columns: corpusManifest.sources,
  };
}

function verifyItems(items: Array<{ id: string; cites: string[] }>, events: readonly RawEvent[]): VerifyResult[] {
  const eventIds = new Set(events.map((event) => event.event_id));
  return items.map((item) => {
    const failed = item.cites
      .filter((cite) => !eventIds.has(cite))
      .map((cite) => ({ event_id_ref: cite, reason: "missing_event" }));
    return {
      finding_id: item.id,
      citations_total: item.cites.length,
      citations_resolved: item.cites.length - failed.length,
      citations_failed: failed,
      content_hashes_match: true,
      passed: item.cites.length > 0 && failed.length === 0,
    };
  });
}

function criticForLoop(loop: WorkLoop): CriticPass {
  const survival = clamp(loop.confidence - (loop.cites.length <= 1 ? 0.08 : 0));
  return {
    finding_id: loop.loop_id,
    attack_vectors_attempted: [
      "is_this_actually_recurring",
      "is_this_actually_blocked",
      "is_the_intent_actually_what_we_say",
      "are_these_events_actually_related",
      "is_the_dollar_estimate_defensible",
      "would_orgx_actually_have_helped",
      "is_the_owner_actually_ambiguous",
      "is_the_recurrence_just_text_overlap",
    ],
    weakest_claim:
      loop.cites.length <= 1
        ? "Only one resolved citation supports this loop; chronology depth is limited."
        : "The loop is supported, but source coverage may still miss owner or verification context.",
    most_plausible_alternative:
      loop.cites.length <= 1
        ? "This may be a single work signal rather than a recurring bottleneck."
        : "This could be normal staged execution rather than unresolved rework.",
    survival_score: Number(survival.toFixed(2)),
    survived: survival >= 0.6,
    demoted_to: survival >= 0.6 ? "public" : "weak_signal",
  };
}

function counterfactualForLoop(loop: WorkLoop, family: LoopFamily | undefined): Counterfactual | null {
  if (!loop.survived_critic || loop.cites.length === 0) return null;
  const repair = family?.orgx_repair ?? repairForFamily([loop], {
    time_saved_hours_per_week: 1,
    acceleration_percent: 10,
    estimated_monthly_value_usd: 800,
    confidence: loop.confidence,
    basis: [],
    assumptions: [],
  });
  const realism = clamp(loop.confidence + (loop.terminal.state === "blocked" ? 0.06 : 0), 0, 0.92);
  if (realism < 0.7) return null;
  const entityKind =
    repair.named_orgx_capability.includes("decision")
      ? "decision"
      : repair.named_orgx_capability.includes("blocker")
        ? "task"
        : repair.named_orgx_capability.includes("artifact")
          ? "artifact"
          : "initiative";
  return {
    triggered_by_event_id: loop.origin.event_id,
    triggered_at: loop.origin.timestamp,
    actual_user_event_summary: `You worked on "${loop.origin.intent}" without a durable OrgX repair record tied to the source event.`,
    actual_outcome:
      loop.terminal.state === "blocked"
        ? "The loop stayed blocked or returned as new work."
        : "The work stayed visible in session evidence, but not as durable operating memory.",
    orgx_capability: {
      tool: repair.named_orgx_capability,
      args: repair.expected_tool_call_args,
      args_basis: ["loop.origin.intent", "loop.cites", "loop.terminal.state", "loop.bottleneck.class"],
    },
    orgx_outcome: repair.expected_outcome,
    resulting_entity: {
      kind: entityKind,
      inferred_id: `orgx_${entityKind}_${hash(loop.loop_id, 12)}`,
      would_link_to: loop.cites,
    },
    realism_score: Number(realism.toFixed(2)),
    realism_basis: [
      "The triggering event resolves to the normalized corpus.",
      "The proposed OrgX capability maps directly to the loop terminal state and bottleneck.",
      "The output is deterministic: create or link an owner-visible entity rather than asserting improvement.",
    ],
    could_have_failed_because: [
      "The user may not have granted write permission.",
      "The inferred title or owner might need human correction after claim.",
      "The source may lack enough downstream verification until GitHub or runtime hooks are connected.",
    ],
    generated_by_prompt_version: "orgx-investigation-counterfactual-v1",
    survived_critic: true,
  };
}

function buildWhyNot100(input: {
  corpus: CorpusManifest;
  coverage: WorkGraphSourceCoverage;
  loops: readonly WorkLoop[];
  families: readonly LoopFamily[];
  impact: WorkGraphImpactProjection;
}): WhyNotEntry[] {
  const entries: WhyNotEntry[] = [];
  const singleSignal = input.loops.filter((loop) => loop.cites.length <= 1).length;
  const confluenceFamilies = input.families.filter((family) => family.cross_source_confluence).length;
  const fileEdits = input.corpus.totals.file_edits;
  if (confluenceFamilies === 0) {
    entries.push({
      dimension: "cross_source_confluence",
      current_score: 0,
      precise_gap: `0 cross-source loop families were proven; ${input.loops.length} loops still need a second source such as GitHub, Slack, or runtime hooks.`,
      user_action: {
        kind: "connect_source",
        target: "GitHub or Slack",
        ux_route: "/integrations",
        copy_for_button: "Connect proof source",
      },
      orgx_action: {
        kind: "auto_correlate",
        description: "OrgX will join session events to commits, approvals, and runtime writeback events.",
        triggers_when: "on_next_audit_run",
      },
      expected_score_lift: 22,
      expected_findings_unlocked: Math.max(1, Math.ceil(input.loops.length * 0.35)),
      preview_findings: [
        {
          title_redacted: "Session decision linked to commit proof",
          evidence_count_anticipated: Math.max(2, fileEdits || 2),
          confidence_anticipated: 0.82,
          derivation_basis: "extrapolated_from_existing",
        },
      ],
      preconditions: ["connect_github_or_slack"],
      estimated_seconds_to_complete: 90,
    });
  }
  if (singleSignal > 0) {
    entries.push({
      dimension: "chronology_depth",
      current_score: Math.max(1, 10 - Math.min(9, singleSignal)),
      precise_gap: `${singleSignal} work loop${singleSignal === 1 ? "" : "s"} have only one resolved event; OrgX can identify them but cannot yet prove before/after motion.`,
      user_action: {
        kind: "install_hook",
        target: "OrgX runtime hooks",
        ux_route: "/settings/integrations/orgx-runtime",
        copy_for_button: "Install runtime hooks",
      },
      orgx_action: {
        kind: "auto_writeback",
        description: "OrgX will append session-start, tool-use, stop, and outcome events to the same loop.",
        triggers_when: "on_user_action_completion",
      },
      expected_score_lift: Math.min(24, singleSignal * 3),
      expected_findings_unlocked: singleSignal,
      preview_findings: [
        {
          title_redacted: "Blocked loop gains start, retry, and resolution events",
          evidence_count_anticipated: 3,
          confidence_anticipated: 0.78,
          derivation_basis: "rule_based",
        },
      ],
      preconditions: ["install_runtime_hook"],
      estimated_seconds_to_complete: 120,
    });
  }
  if (input.coverage.missing.length > 0) {
    entries.push({
      dimension: "source_coverage",
      current_score: Math.max(0, 10 - input.coverage.missing.length * 2),
      precise_gap: `${input.coverage.missing.length} source gap${input.coverage.missing.length === 1 ? "" : "s"} remain: ${input.coverage.missing.join(", ")}.`,
      user_action: {
        kind: "connect_source",
        target: input.coverage.missing[0] ?? "missing source",
        ux_route: "/integrations",
        copy_for_button: "Connect missing source",
      },
      orgx_action: {
        kind: "auto_classify",
        description: "OrgX will classify ownership, handoff, and verification evidence from the newly connected source.",
        triggers_when: "on_next_audit_run",
      },
      expected_score_lift: Math.min(18, input.coverage.missing.length * 4),
      expected_findings_unlocked: Math.max(1, input.coverage.missing.length * 2),
      preview_findings: [
        {
          title_redacted: "Owner-visible handoff replaces inferred blocker state",
          evidence_count_anticipated: 2,
          confidence_anticipated: 0.8,
          derivation_basis: "rule_based",
        },
      ],
      preconditions: ["authorize_source"],
      estimated_seconds_to_complete: 75,
    });
  }
  return entries.slice(0, 6);
}

function buildRepairPlan(input: {
  counterfactuals: readonly Counterfactual[];
  families: readonly LoopFamily[];
  whyNot100: readonly WhyNotEntry[];
}): RepairAction[] {
  const firstFamily = input.families.find((family) => family.public_surface) ?? input.families[0];
  const firstCounterfactual = input.counterfactuals[0];
  const actions: RepairAction[] = [];
  if (firstFamily || firstCounterfactual) {
    const closes = firstFamily?.loop_ids ?? (firstCounterfactual ? [firstCounterfactual.triggered_by_event_id] : []);
    actions.push({
      id: "repair:promote-loop",
      type: firstFamily?.orgx_repair.named_orgx_capability.includes("assign")
        ? "assign_owner"
        : "promote_decision",
      title: "Repair the top recurring work loop",
      closes_loop_ids: closes,
      expected_gain: "execution continuity",
      expected_hours_recovered: firstFamily?.orgx_repair.expected_hours_recovered ?? 0.8,
      expected_dollars_recovered: firstFamily?.orgx_repair.expected_dollars_recovered ?? 650,
      required_inputs: ["confirmed evidence", "owner or fallback owner"],
      confidence: firstCounterfactual?.realism_score ?? 0.72,
      time_to_value: "10 minutes after claim",
      preview_tree: [
        { kind: "workstream", title: "Repair recurring AI work loop" },
        { kind: "milestone", title: "Promote decision or blocker to durable record" },
        { kind: "task", title: "Attach source evidence and owner-visible next step" },
      ],
    });
  }
  const sourceGap = input.whyNot100.find((entry) => entry.dimension === "source_coverage" || entry.dimension === "cross_source_confluence");
  if (sourceGap) {
    actions.push({
      id: "repair:connect-source",
      type: "connect_source",
      title: sourceGap.user_action?.copy_for_button ?? "Connect missing proof source",
      closes_loop_ids: [],
      expected_gain: "source confidence",
      expected_hours_recovered: 0,
      expected_dollars_recovered: 0,
      required_inputs: [sourceGap.user_action?.target ?? "source authorization"],
      confidence: 0.78,
      time_to_value: `${Math.ceil(sourceGap.estimated_seconds_to_complete / 60)} minutes`,
      preview_tree: [
        { kind: "workstream", title: "Connect source evidence" },
        { kind: "task", title: "Correlate session events to proof, owner, and handoff signals" },
      ],
    });
  }
  actions.push({
    id: "repair:launch-profile",
    type: "convert_to_initiative",
    title: "Launch from this investigation",
    closes_loop_ids: firstFamily?.loop_ids ?? [],
    expected_gain: "initiative readiness",
    expected_hours_recovered: firstFamily?.orgx_repair.expected_hours_recovered ?? 0,
    expected_dollars_recovered: firstFamily?.orgx_repair.expected_dollars_recovered ?? 0,
    required_inputs: ["claim profile", "approve repair plan"],
    confidence: 0.7,
    time_to_value: "1 click after claim",
    preview_tree: [
      { kind: "workstream", title: "Investigation to durable OrgX initiative" },
      { kind: "milestone", title: "Convert loop family into assigned work" },
      { kind: "task", title: "Close the highest-confidence counterfactual gap" },
    ],
  });
  return actions.slice(0, 3);
}

function buildMirror(input: {
  loops: readonly WorkLoop[];
  families: readonly LoopFamily[];
  counterfactuals: readonly Counterfactual[];
  corpus: CorpusManifest;
  impact: WorkGraphImpactProjection;
}) {
  const topFamily = input.families.find((family) => family.public_surface);
  const topLoop = input.loops.find((loop) => loop.public_surface) ?? input.loops[0];
  const sourceCount = input.corpus.sources.filter((source) => source.status === "connected" || source.status === "partial").length;
  const dropped = input.loops.filter((loop) => !loop.survived_critic).length;
  const terminal = topFamily?.shared_terminal_state ?? "ongoing";
  const terminalPhrase = `${articleFor(terminal)} ${terminal}`;
  const topSourceLabels = input.corpus.sources
    .filter((source) => source.status === "connected" || source.status === "partial")
    .slice(0, 4)
    .map((source) => source.brand.name)
    .join(", ");
  const text = [
    `Your AI-assisted work spans ${sourceCount} connected or partial source${sourceCount === 1 ? "" : "s"}${topSourceLabels ? ` (${topSourceLabels})` : ""}, but the execution record is still incomplete.`,
    topFamily
      ? `The clearest repeated loop is "${topFamily.semantic_centroid}": ${topFamily.appearances} appearance${topFamily.appearances === 1 ? "" : "s"} ending in ${terminalPhrase} state.`
      : topLoop
        ? `The clearest work loop is "${topLoop.origin.intent}", but it needs more chronology before OrgX should call it recurring.`
        : "The corpus did not produce a verified work loop yet.",
    input.counterfactuals[0]
      ? `The earned repair is concrete: OrgX can point to the event where it would have called ${input.counterfactuals[0].orgx_capability.tool} and the entity it would have created.`
      : "Counterfactual repair is waiting on a higher-confidence loop with resolved citations.",
    `The current estimate is ${input.impact.time_saved_hours_per_week} recoverable hours/week, grounded in ${input.impact.basis[0] ?? "resolved work-loop evidence"}.`,
    dropped > 0
      ? `${dropped} weak signal${dropped === 1 ? " was" : "s were"} kept out of the public readout because the citations or critic score did not clear the bar.`
      : "Every surfaced loop cleared citation verification and the critic floor.",
  ].join(" ");
  return {
    text,
    evidence_pills: [
      {
        phrase: `${input.loops.length} work loop${input.loops.length === 1 ? "" : "s"}`,
        cites: input.loops.flatMap((loop) => loop.cites).slice(0, 12),
      },
      {
        phrase: `${sourceCount} source${sourceCount === 1 ? "" : "s"}`,
        cites: [],
      },
      {
        phrase: `${input.counterfactuals.length} earned counterfactual${input.counterfactuals.length === 1 ? "" : "s"}`,
        cites: input.counterfactuals.map((item) => item.triggered_by_event_id),
      },
    ],
  };
}

export function buildWorkGraphInvestigation(
  input: BuildWorkGraphInvestigationInput,
): WorkGraphInvestigationV2 {
  const auditId = `wgi_${hash([input.fingerprint, input.generatedAt], 24)}`;
  const rawEvents = buildRawEvents(input);
  const corpus = buildCorpusManifest({
    auditId,
    auditMethod: input.auditMethod,
    clientExtractions: input.clientExtractions,
    coverage: input.coverage,
    events: rawEvents,
    generatedAt: input.generatedAt,
  });
  const loops = buildWorkLoops({ events: rawEvents, findings: input.findings, trails: input.trails });
  const families = buildLoopFamilies(loops, rawEvents, input.impact);
  const usage = buildUsageCatalogue(rawEvents, input.findings);
  const sourceConfidence = buildSourceConfidence(corpus);
  const loopCritics = loops.map(criticForLoop);
  const familyByLoop = new Map<string, LoopFamily>();
  for (const family of families) {
    for (const loopId of family.loop_ids) familyByLoop.set(loopId, family);
  }
  const verifiedLoops = loops.filter((loop) => {
    const critic = loopCritics.find((entry) => entry.finding_id === loop.loop_id);
    return loop.public_surface && critic?.survived;
  });
  const counterfactuals = verifiedLoops
    .map((loop) => counterfactualForLoop(loop, familyByLoop.get(loop.loop_id)))
    .filter((item): item is Counterfactual => Boolean(item))
    .slice(0, 8);
  const verificationItems = [
    ...loops.map((loop) => ({ id: loop.loop_id, cites: loop.cites })),
    ...counterfactuals.map((item) => ({
      id: `counterfactual:${item.resulting_entity.inferred_id}`,
      cites: [item.triggered_by_event_id],
    })),
  ];
  const verification = verifyItems(verificationItems, rawEvents);
  const whyNot100 = buildWhyNot100({
    corpus,
    coverage: input.coverage,
    loops,
    families,
    impact: input.impact,
  });
  const repairPlan = buildRepairPlan({ counterfactuals, families, whyNot100 });
  const mirror = buildMirror({
    loops,
    families,
    counterfactuals,
    corpus,
    impact: input.impact,
  });
  const eventBySource = countBy(rawEvents.map((event) => event.source_id)).map(({ key, count }) => ({
    source_id: key,
    count,
  }));
  const eventByKind = countBy(rawEvents.map((event) => event.kind)).map(({ key, count }) => ({
    kind: key,
    count,
  }));

  return {
    schema_version: WORK_GRAPH_INVESTIGATION_SCHEMA_VERSION,
    audit_id: auditId,
    fingerprint: input.fingerprint,
    generated_at: input.generatedAt,
    corpus_manifest: corpus,
    raw_events_summary: {
      count: rawEvents.length,
      by_source: eventBySource,
      by_kind: eventByKind,
      earliest: corpus.totals.earliest_event,
      latest: corpus.totals.latest_event,
    },
    raw_events: rawEvents.slice(0, 500),
    work_loops: loops,
    loop_families: families,
    usage_catalogue: usage,
    source_confidence: sourceConfidence,
    why_not_100: whyNot100,
    counterfactuals,
    critic_log: [
      ...loopCritics,
      ...families.map((family) => ({
        finding_id: family.family_id,
        attack_vectors_attempted: [
          "is_this_actually_recurring",
          "is_the_recurrence_just_text_overlap",
          "are_these_events_actually_related",
        ],
        weakest_claim:
          family.appearances < 3
            ? "The family has fewer than 3 appearances and should not be public as recurrence."
            : "The family still depends on source normalization and may need human correction.",
        most_plausible_alternative:
          family.appearances < 3
            ? "This is a single signal or thin pair, not a real recurring loop."
            : "The loops may be related staged work rather than repeated unresolved work.",
        survival_score: family.survived_critic ? 0.72 : 0.52,
        survived: family.survived_critic,
        demoted_to: family.public_surface ? "public" : "weak_signal",
      } satisfies CriticPass)),
    ],
    verification_log: verification,
    mirror_paragraph: mirror,
    repair_plan: repairPlan,
    impact_projection: {
      hours_recoverable_per_week: {
        value: input.impact.time_saved_hours_per_week,
        basis: input.impact.basis.join(" "),
        confidence: input.impact.confidence,
      },
      dollars_recoverable_per_month: {
        value: input.impact.estimated_monthly_value_usd,
        basis: "hours_recoverable_per_week * 4.33 weeks * blended operator hourly value",
        confidence: input.impact.confidence,
      },
      acceleration_pct: {
        value: input.impact.acceleration_percent,
        basis: "directional execution lift from repairable loops, source coverage, and automation potential",
        confidence: input.impact.confidence,
      },
    },
    redaction_log: [
      { kind: "raw_transcripts_excluded", count: 1 },
      { kind: "redacted_event_payloads", count: rawEvents.length },
    ],
    raw_transcripts_excluded: true,
    claimable: loops.some((loop) => loop.public_surface),
  };
}
